/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect } from 'react-router'
import { NotFound } from 'fejl'
import type { CreateUserManagementRoutesOptions } from '@/features/user-management/routes'
import type { UserDetailsViewModel } from '@/features/user-management/UserDetails/UserDetailsViewModel'
import { Id } from '@/infra/id'
import { links } from '@/utils/link'

/**
 * Data returned by {@link createUserDetailsViewLoader}. `null` when no
 * workspace is selected yet, so the authentication screen is showing.
 */
export type UserDetailsViewLoaderData = { vm: UserDetailsViewModel } | null

/**
 * Creates a loader function for the user details view that provides the view
 * model for the route's user, redirects users who may not see it, and
 * redirects to the user list when the user doesn't exist.
 *
 * @param options
 */
export function createUserDetailsViewLoader(
  options: CreateUserManagementRoutesOptions,
): LoaderFunction {
  return async ({
    params,
    request: { signal },
  }): Promise<UserDetailsViewLoaderData> => {
    const { workspace } = await options.waitForAuthResolution(signal)
    if (!workspace) {
      return null
    }

    const internal = await options.userManagement.internal()
    const vm = await internal.acquireUserDetailsViewModel(
      Id(params.userPublicId),
      signal,
    )
    if (!vm.canAccessUserDetails) {
      throw redirect(links.welcome())
    }

    if (!vm.hasUser) {
      // The view renders any other failed load from the task's own state.
      await vm.fetch(signal).catch((err: unknown) => {
        if (err instanceof NotFound) {
          throw redirect(links.userManagement())
        }
      })
    }

    return { vm }
  }
}
